import type * as React from "react";
import {
  ListBox as AriaListBox,
  type ListBoxProps as AriaListBoxProps,
  ListLayout,
  Provider,
  Virtualizer,
} from "react-aria-components";
import { itemHeightM, itemHeightS, listBox } from "./ListBox.css.ts";
import {
  DropdownItemContext,
  type DropdownItemContextProps,
} from "./shared/DropdownItemContext.ts";
import type {
  MinimumObject,
  OmittedReactAriaPropsWithChildren,
} from "./types.ts";

export interface Props<T extends MinimumObject> extends Omit<
  AriaListBoxProps<T>,
  OmittedReactAriaPropsWithChildren
> {
  /** Size of the list items. */
  size?: DropdownItemContextProps["size"];
  /** Ref to the scrollable container for programmatic scroll control. */
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  /** Enables virtualized rendering for large lists. */
  virtualized?: boolean;
}

export function ListBox<T extends MinimumObject>({
  size = "m",
  scrollRef,
  virtualized = false,
  ...props
}: Props<T>): React.ReactElement {
  return (
    <OptionalVirtualizedList virtualized={virtualized} size={size}>
      <Provider
        values={[
          [DropdownItemContext, { size, selectionMode: props.selectionMode }],
        ]}
      >
        <AriaListBox ref={scrollRef} {...props} className={listBox} />
      </Provider>
    </OptionalVirtualizedList>
  );
}

const OptionalVirtualizedList: React.FC<{
  children: React.ReactNode;
  size: DropdownItemContextProps["size"];
  virtualized: boolean;
}> = ({ children, size, virtualized }) => {
  if (!virtualized) {
    return <>{children}</>;
  }

  const rowSize = size === "s" ? itemHeightS : itemHeightM;

  return (
    <Virtualizer layout={ListLayout} layoutOptions={{ rowSize }}>
      {children}
    </Virtualizer>
  );
};
